import { useState, useEffect, useCallback } from 'react';
import { useShopPluginApi } from '@app/react-common/api/useShopPluginApi';
import { useAppContext } from '@app/react-common/AppContext';
import { ProductPromiseListItem } from '@app/pages/engage/product-page/product-promise.service';

export interface ProductPage {
  id: number | null;
  name: string;
  internalName?: string;
  internalDescription?: string;
  isActive?: boolean;
  testMode?: boolean;
  usageToken?: string;
  createdAt?: string;
}

const useProductPages = (shouldFetch: boolean = true) => {
  const [productPages, setProductPages] = useState<ProductPage[]>([]);
  const [loading, setLoading] = useState<boolean>(shouldFetch);
  const { showToast } = useAppContext();

  const failureHandler = useCallback(async () => {
    showToast({
      message: `Failed to load product pages. Please contact support if the problem persists.`,
      mode: 'danger',
    });
    setLoading(false);
  }, [showToast]);

  const shopPluginApi = useShopPluginApi(failureHandler);

  const fetchProductPages = async () => {
    try {
      setLoading(true);
      const response = await shopPluginApi.get('/admin/product-promise/settings');
      const pages = (response.data || []).map((item: ProductPromiseListItem) => ({
        id: item.id,
        name: item.internalName || `Product Promise ${item.id}`,
        internalName: item.internalName,
        internalDescription: item.internalDescription,
        isActive: item.isActive,
        testMode: item.testMode,
        usageToken: item.usageToken,
        createdAt: item.createdAt,
      }));

      // Add "No Product Promise" option at the beginning of the list
      const noProductPromiseOption: ProductPage = {
        id: null,
        name: 'No Product Promise',
        internalName: 'No Product Promise',
      };

      setProductPages([noProductPromiseOption, ...pages]);
    } catch (error) {
      console.error('Error fetching product pages:', error);
      setProductPages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch product pages if shouldFetch is true
    if (shouldFetch) {
      fetchProductPages();
    } else {
      setLoading(false);
    }
  }, [shouldFetch]);

  return {
    productPages,
    loading,
  };
};

export default useProductPages;
